/**
 * useStudyGroups — the client half of the study groups (job #517, Tom 2026-09-21).
 *
 * THE SHAPE OF THE THING. A learner may be in up to THREE groups; a group
 * holds up to SEVEN. A group's DEFAULT name is its language, its belt and its
 * members' first names, and each member may give it a name of their own that
 * nobody else sees (job #45, Tom + Aran 2026-09-24). NO DIRECTORY, TWO DOORS (Tom, 2026-09-24): there is no list of
 * groups to browse. `join` puts the learner into the existing group in their
 * language, with a seat, whose members are closest to where they are in the
 * course now — any belt (job #119); `start` opens a new one;
 * and any member hands out an invite link (the room's `invite.token`), which
 * `previewInvite` reads and `joinByInvite` spends — every rule re-checked on
 * the server.
 *
 * TEXT IS FREE, VOICE IS THE £5 ENTITLEMENT (job #490). This composable never
 * decides that either — it reports the server's 402 as `voiceRequired`, and
 * the screen mounts <VoicePadlock> at the tap. Listening to a voice note is
 * free and needs no flag at all.
 *
 * A BLOCK IS MUTUAL INVISIBILITY, and it is done ON THE SERVER: blocked
 * learners are absent from the payload entirely. There is deliberately no
 * client-side filtering here — a v-if that forgot to run is how invisibility
 * leaks, so there is no v-if to forget.
 *
 * SHARED, MODULE-LEVEL GROUP LIST, like useVoiceEntitlement's flag: the mode
 * tray asks "are you in any?" without a round trip of its own, because the
 * groups screen has already answered.
 *
 * Shaped after useAvailability.ts — same auth-header read, same error
 * discipline: a failed call sets `error` to what the server said and returns
 * false, and nothing throws at a template.
 */
import { ref, computed, inject, type Ref } from 'vue'
import { getLanguageName, t } from './useI18n'
import { courseTargetName, type CourseNameFields } from '@/utils/courseDisplayName'
import { isVoiceRequired, padlockPlanFor, noteEntitlementRefused, useVoiceEntitlement, type PadlockPlan } from './useVoiceEntitlement'

/**
 * A study group's VARIETY (job #124): the target language with its regional
 * variant — cym_n, cym_s, deu_at, or plain spa. Groups never mix varieties.
 * `nameCourse` is one course of a regional variety, sent by the server so the
 * name is authored ("Welsh (Northern)"), never guessed; null for a plain one.
 */
export interface VarietyNamed {
  targetLang: string
  variety?: string | null
  nameCourse?: CourseNameFields | null
}

/** A variety's learner-facing name: "Welsh (Northern)", "Austrian German", "Spanish". */
export function varietyName(v: VarietyNamed): string {
  return (v.nameCourse ? courseTargetName({ ...v.nameCourse, target_lang: v.targetLang }) : '') || getLanguageName(v.targetLang)
}

export interface GroupCard extends VarietyNamed {
  id: string
  targetLang: string
  belt: string
  memberCount: number
  /** First names, joining order, blocked learners already left out by the server. */
  memberNames?: string[]
  /** The learner's own name for a room they are in (job #45). Null = the default. */
  nickname?: string | null
  liveCount: number
  huddleLive: boolean
}

export interface EligibleLanguage extends VarietyNamed {
  targetLang: string
  /** Always sent since job #124; optional only so an older server's answer still types. */
  variety?: string
  /** Every course of theirs in this variety — how the screen finds the one they are playing. */
  courseIds?: string[]
  belt: string
  seeds: number
  courseId: string
}

export interface GroupsState {
  eligible: boolean
  reason: string | null
  message: string | null
  firstName: string
  languages: EligibleLanguage[]
  groups: GroupCard[]
  maxGroups: number
  maxMembers: number
  /** May they join another? False with a barredMessage is the SILENT bar. */
  canJoin: boolean
  barredMessage: string | null
}

export interface GroupMember {
  id: string
  firstName: string
  belt: string
  mine: boolean
}

export interface GroupPost {
  id: string
  kind: 'text' | 'voice'
  body: string | null
  audioUrl: string | null
  audioMs: number | null
  firstName: string
  mine: boolean
  createdAt: string
  expiresAt: string
  /**
   * Client-only: when THIS client first took `audioUrl`. The server mints a
   * fresh signed url on every read, and the room is read every five seconds;
   * see stabiliseRoom for why the url is kept rather than swapped.
   */
  audioUrlAt?: number
}

export interface LivePerson {
  id: string
  firstName: string
  inHuddle: boolean
  mine: boolean
}

export interface GroupRoom {
  group: { id: string; targetLang: string; belt: string; nickname?: string | null } & VarietyNamed
  members: GroupMember[]
  /** Oldest first — ready to render top to bottom, like a chat. */
  posts: GroupPost[]
  live: LivePerson[]
  huddle: { live: boolean; hidden: boolean }
  /** The invite any member may hand out; null once the group is full. */
  invite: { token: string } | null
  maxMembers: number
}

/** Why an invite can or cannot be used — worded kindly by the screen. */
export type InviteVerdict = 'ok' | 'already_in' | 'full' | 'not_open' | 'school_account' | 'not_on_course' | 'at_limit' | 'barred'

export interface InvitePreview {
  group: ({ id: string; targetLang: string; belt: string; memberCount: number } & VarietyNamed) | null
  verdict: InviteVerdict
  /** The silent bar's one sentence, and only that. */
  message: string | null
  /** Text is free; voice notes and the huddle need Voice. Never a gate on joining. */
  voiceLocked: boolean
  maxMembers: number
}

/** The link a member shares. Built here, so the server never needs to know its own origin. */
export function inviteUrl(token: string, origin: string = typeof window !== 'undefined' ? window.location.origin : ''): string {
  return `${origin}/groups/invite/${encodeURIComponent(token)}`
}

export type ReportReason = 'abusive' | 'inappropriate' | 'not_learning' | 'other'

/**
 * A group's DEFAULT name (job #45, Tom + Aran 2026-09-24): its language, its
 * belt and the first names of the people in it — "Welsh, yellow, Aran, Kai,
 * Tom". Language and belt alone left Aran unable to tell her own rooms apart.
 * Nothing is stored: the name is rendered from the facts, so it cannot drift.
 */
export function groupName(
  targetLang: string,
  belt: string,
  memberNames: string[] = [],
  nameCourse: CourseNameFields | null = null,
): string {
  const colour = t(`belt.${belt}`, belt).toLocaleLowerCase()
  return [varietyName({ targetLang, nameCourse }), colour, ...memberNames.filter(Boolean)].join(', ')
}

/**
 * What THIS learner calls the room: their own name for it if they gave it
 * one, the default otherwise. Everybody else sees their own.
 */
export function displayGroupName(
  g: { targetLang: string; belt: string; memberNames?: string[]; nickname?: string | null; nameCourse?: CourseNameFields | null },
): string {
  return g.nickname?.trim() || groupName(g.targetLang, g.belt, g.memberNames ?? [], g.nameCourse ?? null)
}

/**
 * How long this client keeps a voice note's signed url before taking the
 * server's newer one. The server signs for ten minutes (VOICE_URL_SECONDS);
 * eight leaves two minutes of margin for a slow read.
 */
export const AUDIO_URL_KEEP_MS = 8 * 60_000

/**
 * THE POLL MUST NOT TOUCH A VOICE NOTE (job #45, Tom + Aran on Android,
 * 2026-09-24: "flickers / jumps about every 6 seconds"). The room is read
 * every five seconds and the server signs every voice note afresh on each
 * read, so each read carried a DIFFERENT url for the same note. Bound to
 * <audio :src>, that swapped the element's source every five seconds: it
 * dropped what it had loaded, reset its controls to 0:00, re-fetched, and
 * stopped anything mid-play. A note keeps the url this client already holds
 * until that url is near its expiry; only then is the server's newer one
 * taken — once every eight minutes, not every five seconds.
 */
export function stabiliseRoom(prev: GroupRoom | null, next: GroupRoom, nowMs: number): GroupRoom {
  if (!Array.isArray(next?.posts)) return next
  const held = new Map((prev?.posts ?? []).map((p) => [p.id, p]))
  return {
    ...next,
    posts: next.posts.map((p) => {
      const old = held.get(p.id)
      if (!p.audioUrl) return p
      if (old?.audioUrl && old.audioUrlAt !== undefined && nowMs - old.audioUrlAt < AUDIO_URL_KEEP_MS) {
        return { ...p, audioUrl: old.audioUrl, audioUrlAt: old.audioUrlAt }
      }
      return { ...p, audioUrlAt: nowMs }
    }),
  }
}

/**
 * Shared across every caller, so the mode tray reads it for free — exported
 * because the tray wants the answer and not the whole composable, and because
 * inject() only works inside setup().
 */
export const groupsState = ref<GroupsState | null>(null)
const state = groupsState

export const myGroupCount = computed(() => state.value?.groups.length ?? 0)

export function useStudyGroups() {
  const supabaseRef = inject<Ref<any> | null>('supabase', null)

  const room = ref<GroupRoom | null>(null)
  const loading = ref(false)
  const busy = ref(false)
  const error = ref<string | null>(null)
  /** The server said 402: the screen mounts VoicePadlock at the tap. */
  const voiceRequired = ref(false)
  /** Which padlock the 402 asked for: £5 voice in a free language, the £15 plan in a premium one (job #576). */
  const voicePadlockPlan = ref<PadlockPlan>('voice')
  const voice = useVoiceEntitlement()
  /**
   * A FAILED SEND MUST STAY ON THE SCREEN. The room re-reads itself every five
   * seconds, and that read used to clear `error` on success — so the line a
   * failed voice note put up was wiped before anybody could read it, and the
   * send looked like silence (job #541, Tom 2026-09-21). An error an ACTION
   * raised is sticky: only the next action clears it, never the poll.
   */
  let errorSticky = false
  function setActionError(msg: string): void {
    error.value = msg
    errorSticky = true
  }
  function clearActionError(): void {
    error.value = null
    errorSticky = false
  }
  /** They are out of this room — removed or left. The screen says so and goes. */
  const gone = ref<string | null>(null)

  async function authHeaders(): Promise<Record<string, string> | null> {
    const sb = supabaseRef?.value
    if (!sb) return null
    try {
      const { data } = await sb.auth.getSession()
      const token = data?.session?.access_token
      return token ? { Authorization: `Bearer ${token}` } : null
    } catch {
      return null
    }
  }

  // A newer list refresh owns the result, error and loading state.
  let listRequest = 0

  async function refresh(): Promise<void> {
    const request = ++listRequest
    const headers = await authHeaders()
    if (request !== listRequest) return
    if (!headers) {
      loading.value = false
      error.value = t('groups.signIn', 'Sign in to use study groups.')
      return
    }
    loading.value = true
    try {
      const resp = await fetch('/api/social/groups', { headers })
      const data = await resp.json().catch(() => ({}))
      if (request !== listRequest) return
      if (!resp.ok) throw new Error(data.error || t('groups.loadFailed', 'Could not load your study groups'))
      state.value = data as GroupsState
      error.value = null
    } catch (err) {
      if (request !== listRequest) return
      error.value = err instanceof Error ? err.message : t('groups.loadFailed', 'Could not load your study groups')
    } finally {
      if (request === listRequest) loading.value = false
    }
  }

  /** The server's reason on the last refused join — 'no_room' is not an error, it is a fork. */
  const joinReason = ref<string | null>(null)

  async function postJoin(body: Record<string, unknown>): Promise<GroupCard | null> {
    const headers = await authHeaders()
    if (!headers) return null
    busy.value = true
    joinReason.value = null
    try {
      const resp = await fetch('/api/social/groups', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify(body),
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) {
        joinReason.value = typeof data.reason === 'string' ? data.reason : null
        error.value = data.error || t('groups.joinFailed', 'That did not go through')
        return null
      }
      error.value = null
      await refresh()
      return (data.group ?? null) as GroupCard | null
    } catch (err) {
      error.value = err instanceof Error ? err.message : t('groups.joinFailed', 'That did not go through')
      return null
    } finally {
      busy.value = false
    }
  }

  /** "Join a group": the group in this variety with a seat whose members are nearest where they are now. */
  function join(variety: string, adultConfirmed: boolean): Promise<GroupCard | null> {
    return postJoin({ action: 'join', variety, adultConfirmed })
  }

  /** "Start a new group" in this variety: always a new one, labelled with the belt of where they are now. */
  function start(variety: string, adultConfirmed: boolean): Promise<GroupCard | null> {
    return postJoin({ action: 'start', variety, adultConfirmed })
  }

  /** Take the seat an invite link offers. */
  function joinByInvite(invite: string, adultConfirmed: boolean): Promise<GroupCard | null> {
    return postJoin({ action: 'join', invite, adultConfirmed })
  }

  /** Read an invite link: which group, how full, and may I. */
  async function previewInvite(invite: string): Promise<InvitePreview | null> {
    const headers = await authHeaders()
    if (!headers) {
      error.value = t('groups.inviteSignIn', 'Sign in to open this invite.')
      return null
    }
    loading.value = true
    try {
      const resp = await fetch(`/api/social/groups?invite=${encodeURIComponent(invite)}`, { headers })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.error || t('groups.loadFailed', 'Could not load your study groups'))
      error.value = null
      return data as InvitePreview
    } catch (err) {
      error.value = err instanceof Error ? err.message : t('groups.loadFailed', 'Could not load your study groups')
      return null
    } finally {
      loading.value = false
    }
  }

  async function leave(id: string): Promise<boolean> {
    const headers = await authHeaders()
    if (!headers) return false
    busy.value = true
    try {
      const resp = await fetch('/api/social/groups', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ action: 'leave', id }),
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) {
        setActionError(data.error || t('groups.leaveFailed', 'That did not go through'))
        return false
      }
      clearActionError()
      await refresh()
      return true
    } catch {
      setActionError(t('groups.leaveFailed', 'That did not go through'))
      return false
    } finally {
      busy.value = false
    }
  }

  // ── ONE ROOM ──────────────────────────────────────────────────────────────

  let reading = false

  /**
   * Read the room. Poll this while the screen is open so two people in it see
   * each other's posts land — and never more than one read in flight, which
   * is what `reading` is for: a slow answer must not stack up behind itself.
   */
  async function openRoom(id: string): Promise<void> {
    if (reading) return
    // Claim the read before authentication yields, so overlapping polls cannot
    // both pass the guard and issue the same room request.
    reading = true
    try {
      const headers = await authHeaders()
      if (!headers) {
        error.value = t('groups.signIn', 'Sign in to use study groups.')
        return
      }
      if (!room.value) loading.value = true
      const resp = await fetch(`/api/social/group?id=${encodeURIComponent(id)}`, { headers })
      const data = await resp.json().catch(() => ({}))
      if (resp.status === 403 && (data.reason === 'removed' || data.reason === 'left')) {
        // The end of the matter: say what the server said, plainly, and the
        // screen sends them back to the list.
        gone.value = data.error || ''
        return
      }
      if (!resp.ok) throw new Error(data.error || t('groups.loadFailed', 'Could not load your study groups'))
      room.value = stabiliseRoom(room.value, data as GroupRoom, Date.now())
      if (!errorSticky) error.value = null
    } catch (err) {
      error.value = err instanceof Error ? err.message : t('groups.loadFailed', 'Could not load your study groups')
    } finally {
      reading = false
      loading.value = false
    }
  }

  /**
   * Everything done inside a room goes through here, so the three answers
   * that are not an error — the padlock, the silent removal, the rate limit —
   * are handled once rather than at five call sites.
   */
  async function act(id: string, body: Record<string, unknown>, retried = false): Promise<any | null> {
    const headers = await authHeaders()
    if (!headers) return null
    // The heartbeat is not something anybody asked for, so its success must
    // never clear the line a failed send left on the screen.
    const isHeartbeat = body.action === 'presence'
    busy.value = true
    try {
      const resp = await fetch('/api/social/group', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ id, ...body }),
      })
      const data = await resp.json().catch(() => ({}))
      if (resp.status === 402 && isVoiceRequired(data)) {
        // The padlock, but not on a stale answer: the flag this client holds
        // may predate the grant that unlocked them (job #541). Re-read once,
        // and only then mount it. A £5 holder refused in a PREMIUM language
        // (code 'plan_required', job #576) re-reads as entitled, retries, is
        // refused again and lands here with retried=true — the £15 padlock.
        if (!retried && data.code !== 'plan_required') {
          const nowEntitled = await voice.refresh()
          if (nowEntitled) return act(id, body, true)
        }
        voicePadlockPlan.value = padlockPlanFor(data)
        voiceRequired.value = true
        noteEntitlementRefused()
        return null
      }
      if (resp.status === 403 && (data.reason === 'removed' || data.reason === 'left')) {
        gone.value = data.error || ''
        return null
      }
      if (!resp.ok) {
        // 429 included: the server's own gentle line, shown as it stands and
        // never retried behind the learner's back.
        setActionError(data.error || t('groups.postFailed', 'That did not go through'))
        return null
      }
      if (!isHeartbeat) clearActionError()
      // An action that removed ME ends the visit, whatever else it did.
      if (data.removedMe) gone.value = data.message || ''
      return data
    } catch (err) {
      setActionError(err instanceof Error ? err.message : t('groups.postFailed', 'That did not go through'))
      return null
    } finally {
      busy.value = false
    }
  }

  /** A failure the SCREEN itself found — an empty recording, no microphone. */
  function showError(msg: string): void {
    setActionError(msg)
  }

  async function postText(id: string, body: string): Promise<boolean> {
    const text = body.trim()
    if (!text) return false
    const data = await act(id, { action: 'post', kind: 'text', body: text })
    if (!data) return false
    await openRoom(id)
    return true
  }

  async function postVoice(id: string, audio: string, mime: string, ms: number): Promise<boolean> {
    const data = await act(id, { action: 'post', kind: 'voice', audio, mime, ms })
    if (!data) return false
    await openRoom(id)
    return true
  }

  /** The heartbeat. 45s freshness server-side, so the screen beats every ~20s. */
  async function presence(id: string, gone_ = false): Promise<void> {
    await act(id, { action: 'presence', ...(gone_ ? { gone: true } : {}) })
  }

  /**
   * Name the room FOR YOURSELF (job #45). Blank goes back to the default.
   * The room and the list both carry the new name straight away, so the tap
   * is answered before the next poll.
   */
  async function rename(id: string, nickname: string): Promise<boolean> {
    const data = await act(id, { action: 'rename', nickname })
    if (!data) return false
    const saved = (data.nickname ?? null) as string | null
    if (room.value?.group.id === id) room.value = { ...room.value, group: { ...room.value.group, nickname: saved } }
    const st = state.value
    if (st) state.value = { ...st, groups: st.groups.map((g) => (g.id === id ? { ...g, nickname: saved } : g)) }
    return true
  }

  async function block(id: string, learnerId: string): Promise<string | null> {
    const data = await act(id, { action: 'block', learnerId })
    if (!data) return null
    await openRoom(id)
    return data.message ?? null
  }

  async function report(
    id: string,
    target: string | { postId: string } | { learnerId: string },
    reason: ReportReason,
    alsoBlock = true,
  ): Promise<string | null> {
    // A post, or a person reported from inside the huddle (job #908).
    const about = typeof target === 'string' ? { postId: target } : target
    const data = await act(id, { action: 'report', ...about, reason, block: alsoBlock })
    if (!data) return null
    await openRoom(id)
    return data.message ?? null
  }

  return {
    state,
    room,
    loading,
    busy,
    error,
    voiceRequired,
    voicePadlockPlan,
    showError,
    gone,
    myGroups: computed(() => state.value?.groups ?? []),
    joinReason,
    refresh,
    join,
    start,
    joinByInvite,
    previewInvite,
    leave,
    openRoom,
    postText,
    postVoice,
    presence,
    block,
    report,
    rename,
  }
}
